export const SERVICE_FEE_PRODUCT_KEY = 'serviceFee';
export const WALK_IN_SERVICE_FEE_PRODUCT_KEY = 'WALK_IN_SERVICE_FEE';
export const WALK_IN_DAILY_RATE_PRODUCT_KEY = 'WALK_IN_DAILY_RATE';
export const WALK_IN_HOURLY_RATE_PRODUCT_KEY = 'WALK_IN_HOURLY_RATE';
export const WALK_IN_INSURANCE_FEE_PRODUCT_KEY = 'WALK_IN_INSURANCE_FEE';
export const WALK_IN_PREMIUM_INSURANCE_FEE_PRODUCT_KEY = 'WALK_IN_PREMIUM_INSURANCE_FEE';
export const GUEST_SERVICE_FEE_PRODUCT_KEY = 'GUEST_SERVICE_FEE';
export const GUEST_DAILY_RATE_PRODUCT_KEY = 'GUEST_DAILY_RATE';
export const GUEST_HOURLY_RATE_PRODUCT_KEY = 'GUEST_HOURLY_RATE';
export const GUEST_INSURANCE_FEE_PRODUCT_KEY = 'GUEST_INSURANCE_FEE';
export const GUEST_PREMIUM_INSURANCE_FEE_PRODUCT_KEY = 'GUEST_PREMIUM_INSURANCE_FEE';
export const INSURANCE_PRODUCT_KEY = 'startupFee';
export const PREMIUM_INSURANCE_PRODUCT_KEY = 'premiumInsuranceFee';
export const STORAGE_HOUR_PRODUCT_KEY = 'hourlyRate';
export const STORAGE_DAY_PRODUCT_KEY = 'dailyRate';
/**
 * Same-day twins of the storage products (CTR-51, AM-4961). The API puts one of these on the order instead
 * of `hourlyRate` / `dailyRate` when the drop-off is today in the location's timezone; it is priced like its
 * twin and must render exactly like it.
 */
export const SAME_DAY_STORAGE_HOUR_PRODUCT_KEY = 'SAME_DAY_HOURLY_RATE';
export const SAME_DAY_STORAGE_DAY_PRODUCT_KEY = 'SAME_DAY_DAILY_RATE';
export const DISCOUNT_PRODUCT_KEY = 'discountCode';
export const TIP_PRODUCT_KEY = 'tip';
export const CANCELLATION_FEE_PRODUCT_KEY = 'CANCELLATION_FEE';
export const FREE_CANCELLATION_FEE_PRODUCT_KEY = 'freeCancellationFee';
export const SECURITY_SEAL_PRODUCT_KEY = 'securitySeal';

export const PREMIUM_INSURANCE_PRODUCT_KEYS = [
  GUEST_PREMIUM_INSURANCE_FEE_PRODUCT_KEY,
  PREMIUM_INSURANCE_PRODUCT_KEY,
  WALK_IN_PREMIUM_INSURANCE_FEE_PRODUCT_KEY,
];

/**
 * The coverage a traveler picked on an insurance step. `none` is a **decision** (declined cover), as
 * distinct from not having chosen yet — which callers represent with `null`/`undefined`, never here.
 *
 * ---
 * **Known design debt: this file hard-codes the catalogue the traveler can be offered.**
 *
 * A fixed `'basic' | 'premium'` union, plus the table below mapping each to a product key, means the
 * set of coverage options lives in the client. Adding a third tier — or renaming, reordering or
 * withdrawing one — is a **client release**, in both the web and native apps, even though the products
 * and their prices are already database rows the backend owns.
 *
 * The intended end state is the inverse: the backend returns the coverage options available for a
 * quote (label, price, cover amount, ordering), and the insurance step renders whatever list it is
 * given — one option, three, or a new "Platinum" tier — with no client knowledge of specific products.
 * The client would then name a *chosen option* back, not a tier it knew about in advance.
 *
 * That refactor is deliberately **not** in AM-4345: it changes the quote/product-list contract, so it
 * needs its own ticket and an api-first deploy. Until then, treat every mention of `basic`/`premium`
 * and every entry in {@link INSURANCE_PRODUCT_KEYS_BY_CONTEXT} as a shim to be deleted, not as a model
 * to extend — a fourth tier is the signal to do the refactor rather than add another literal.
 */
export type InsuranceCoverageTier = 'basic' | 'premium' | 'none';

/**
 * Which family of insurance products prices a booking. Storage, service fee and insurance all come in
 * per-family variants so the three sales contexts can be priced independently — a walk-in traveler
 * standing in the shop, a hotel guest, and a pre-booking made ahead of arrival are different products
 * commercially, not just different screens.
 *
 * `preBooking` rather than `marketplace`: a walk-in is a marketplace customer too, so "marketplace"
 * named the wrong axis. What separates these three is *when and how the booking is made*.
 */
export type InsuranceContext = 'preBooking' | 'walkIn' | 'guest';

/**
 * Basic/Premium product key per insurance context — the single table both the quote request and the
 * order-summary rendering resolve through, so a tier can never be paired with another context's
 * product. **The server resolves the same table** (`luggagehero-api`'s
 * `getInsuranceRateKeyFromOrderRequest`) and is the authority: the client names a tier, the server
 * prices it.
 *
 * Part of the design debt described on {@link InsuranceCoverageTier}: this table is the client's copy
 * of a catalogue the backend already owns, and the two must be changed together until the backend
 * simply returns the offerable options.
 */
export const INSURANCE_PRODUCT_KEYS_BY_CONTEXT: Record<InsuranceContext, { basic: string; premium: string }> = {
  preBooking: { basic: INSURANCE_PRODUCT_KEY, premium: PREMIUM_INSURANCE_PRODUCT_KEY },
  walkIn: { basic: WALK_IN_INSURANCE_FEE_PRODUCT_KEY, premium: WALK_IN_PREMIUM_INSURANCE_FEE_PRODUCT_KEY },
  guest: { basic: GUEST_INSURANCE_FEE_PRODUCT_KEY, premium: GUEST_PREMIUM_INSURANCE_FEE_PRODUCT_KEY },
};

export interface Product {
  _id?: string;
  name: string;
  unitSingular?: string;
  unitPlural?: string;
  info?: {
    header?: string;
    body: string;
  };
}

export interface NewProduct {
  _id?: string;
  name: string;
  key: string;
  description: string;
  created?: Date;
  modified?: Date;
}
